import { AxiosResponse } from 'axios';
import api from '../api';
import {
  IAutosaveExamAttemptAnswerPayload,
  IExamAttemptAutosaveResponse,
  IExamAttemptJoinResponse,
  IExamAttemptSubmitResponse,
} from '../../interface/examAttempt.interface';

/**
 * CVA-4197 join-exam plan 0004. Same controller base as `studentPortal`
 * (`v2/student/exam`) — shared with guardians, explicit `studentId` always,
 * never inferred from the token. The base URL already includes `/v2`.
 */

const basePath = (studentId: string, examSubjectId: string): string =>
  `/student/exam/${studentId}/subject/${examSubjectId}/attempt`;

/** Join (or re-open an already-joined) attempt. Idempotent server-side. */
const joinExamAttempt = async (
  studentId: string,
  examSubjectId: string,
): Promise<AxiosResponse<IExamAttemptJoinResponse>> => {
  return api.post<IExamAttemptJoinResponse>(basePath(studentId, examSubjectId));
};

/** Resume — same response shape as join. */
const resumeExamAttempt = async (
  studentId: string,
  examSubjectId: string,
): Promise<AxiosResponse<IExamAttemptJoinResponse>> => {
  return api.get<IExamAttemptJoinResponse>(basePath(studentId, examSubjectId));
};

/** Autosave one item (delta). */
const autosaveExamAttemptAnswer = async (
  studentId: string,
  examSubjectId: string,
  payload: IAutosaveExamAttemptAnswerPayload,
): Promise<AxiosResponse<IExamAttemptAutosaveResponse>> => {
  return api.patch<IExamAttemptAutosaveResponse>(
    `${basePath(studentId, examSubjectId)}/answer`,
    payload,
  );
};

/** Manual submit. Idempotent — a submitted attempt can never be reopened. */
const submitExamAttempt = async (
  studentId: string,
  examSubjectId: string,
): Promise<AxiosResponse<IExamAttemptSubmitResponse>> => {
  return api.post<IExamAttemptSubmitResponse>(`${basePath(studentId, examSubjectId)}/submit`);
};

export const examAttemptService = {
  joinExamAttempt,
  resumeExamAttempt,
  autosaveExamAttemptAnswer,
  submitExamAttempt,
};
